import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  useTheme,
  alpha,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  AccountBalance as CreditsIcon,
  TrendingUp as TrendingUpIcon,
  History as HistoryIcon,
  Autorenew as AutorenewIcon
} from '@mui/icons-material';
import { creditApi, AgentCreditUsage, OrganizationCredits } from '../../utils/creditApi';
import { useTranslation } from 'react-i18next';

interface AgentCreditUsageProps {
  agentId: string;
}

const AgentCreditUsageComponent: React.FC<AgentCreditUsageProps> = ({ agentId }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [creditUsage, setCreditUsage] = useState<AgentCreditUsage | null>(null);
  const [organizationCredits, setOrganizationCredits] = useState<OrganizationCredits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (agentId) {
      fetchCreditUsage();
    }
  }, [agentId]);

  const fetchCreditUsage = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Buscar dados do agente e da organização em paralelo
      const [agentData, orgData] = await Promise.all([
        creditApi.getAgentCreditUsage(agentId),
        creditApi.getOrganizationCredits()
      ]);
      
      setCreditUsage(agentData);
      setOrganizationCredits(orgData);
    } catch (err) {
      setError(t('agents.creditUsageError'));
      console.error('Error fetching agent credit usage:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      setError(null);
      
      // Buscar dados do agente e da organização em paralelo
      const [agentData, orgData] = await Promise.all([
        creditApi.getAgentCreditUsage(agentId),
        creditApi.getOrganizationCredits()
      ]);
      
      setCreditUsage(agentData);
      setOrganizationCredits(orgData);
    } catch (err) {
      setError(t('agents.creditUsageRefreshError'));
      console.error('Error refreshing agent credit usage:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getUsageColor = (used: number) => {
    if (!organizationCredits) return theme.palette.success.main;
    
    const totalCredits = organizationCredits.credit;
    const percentage = (used / totalCredits) * 100;
    
    if (percentage > 90) return theme.palette.error.main;
    if (percentage > 70) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const getUsageLabel = (used: number) => {
    if (!organizationCredits) return t('agents.usageLow');
    
    const totalCredits = organizationCredits.credit;
    const percentage = (used / totalCredits) * 100;
    
    if (percentage > 90) return t('agents.usageHigh');
    if (percentage > 70) return t('agents.usageMedium');
    return t('agents.usageLow');
  };

  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress size={40} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!creditUsage) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('agents.creditUsageNoData')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CreditsIcon sx={{ fontSize: 24, mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('agents.creditUsage')}
          </Typography>
        </Box>
        <Tooltip title={t('agents.creditUsageRefresh')} arrow>
          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={isRefreshing}
            sx={{
              color: 'primary.main',
              opacity: isRefreshing ? 0.7 : 1,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              },
              transition: 'all 0.2s ease',
            }}
          >
            <AutorenewIcon
              sx={{
                fontSize: '1.2rem',
                animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': {
                    transform: 'rotate(0deg)',
                  },
                  '100%': {
                    transform: 'rotate(360deg)',
                  },
                },
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Uso do mês atual */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('agents.thisMonth')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: getUsageColor(creditUsage.usedThisMonth) }}>
              {creditUsage.usedThisMonth}
            </Typography>
            <Chip
              label={getUsageLabel(creditUsage.usedThisMonth)}
              size="small"
              color={(() => {
                if (!organizationCredits) return 'success';
                const percentage = (creditUsage.usedThisMonth / organizationCredits.credit) * 100;
                if (percentage > 90) return 'error';
                if (percentage > 70) return 'warning';
                return 'success';
              })()}
              variant="outlined"
            />
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={organizationCredits ? Math.min((creditUsage.usedThisMonth / organizationCredits.credit) * 100, 100) : 0}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: alpha(theme.palette.grey[300], 0.3),
            '& .MuiLinearProgress-bar': {
              backgroundColor: getUsageColor(creditUsage.usedThisMonth),
              borderRadius: 4,
            }
          }}
        />
      </Box>

      {/* Histórico mensal */}
      {creditUsage.monthlyHistory.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <HistoryIcon sx={{ fontSize: 20, mr: 1, color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              {t('agents.creditHistory')}
            </Typography>
          </Box>
          
          <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
            {creditUsage.monthlyHistory.map((month, index) => (
              <ListItem key={month.month} sx={{ px: 0 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {month.monthDisplay}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: getUsageColor(month.used) }}>
                          {month.used}
                        </Typography>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: getUsageColor(month.used)
                          }}
                        />
                      </Box>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {creditUsage.monthlyHistory.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('agents.noHistoryAvailable')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default AgentCreditUsageComponent;
